<template>
    <div :class="[$style['mds-combo-box'], classObject, sizeClass]" :disabled="disabled">
        <div
            :class="$style['mds-combo-box__single-select']"
            v-click-outside="hideResults"
            role="combobox"
            :aria-labelledby="ariaLabel"
            :aria-describedby="ariaDescription"
            aria-haspopup="true"
            :aria-expanded="showResults ? 'true' : 'false'"
        >
            <span
                :class="$style['mds-combo-box__single-selected-item']"
                @click="openResults"
                @keyup.enter.prevent="openResults"
                aria-readonly="true"
                role="textbox"
                :aria-labelledby="ariaLabel"
                :aria-describedby="ariaDescription"
                ref="selection"
                :tabindex="this.disabled ? -1 : 0"
            >
                {{ displayValue }}
                <mds-icon
                    :class="$style['mds-combo-box__open-icon']"
                    name="caret-down"
                    title="caret-down"
                    size="small"
                    role="presentation"
                />
            </span>
            <input
                v-if="showResults"
                type="text"
                :id="ariaInputId"
                v-model="searchTerm"
                :class="$style['mds-combo-box__single-input']"
                ref="input"
                :disabled="disabled"
                @input="highlightedItem = 0"
                @keydown.tab="handleTab"
                role="searchbox"
                aria-autocomplete="list"
                :aria-controls="ariaRelationshipId"
                :aria-activedescendant="ariaActiveDescendant"
                :required="required"
            />
            <ul
                v-if="showResults"
                ref="results"
                v-auto-direction="positionResults"
                :class="$style['mds-combo-box__result-list']"
                v-keyboard-navigation="navigate"
                :id="id"
                role="listbox"
                :aria-expanded="showResults ? 'true' : 'false'"
                :aria-hidden="showResults ? 'false' : 'true'"
            >
                <li
                    v-for="(item, index) in filteredList"
                    @click.prevent.stop="setItemState(item.text, item.value)"
                    :ref="index"
                    :key="index"
                    role="option"
                    :aria-selected="isSelected(item.value) ? 'true' : 'false'"
                    :id="`mds-combo-box__descendent-${index}`"
                    :class="{
                        [$style['mds-combo-box__item-selected']]: isSelected(item.value),
                        [$style['mds-combo-box--highlighted']]: isHighlighted(item, index),
                        [$style['mds-combo-box--heading']]: item.value === null,
                        [$style['mds-combo-box--child']]: item.child === true,
                    }"
                    :data-text="item.text"
                    :data-value="item.value"
                >
                    <span :inner-html.prop="item.text | highlight(searchTerm, $style)"></span>
                    <div v-if="item.metadata" :class="$style['mds-combo-box__item-metadata']">
                        <span
                            v-for="(item, index) in item.metadata"
                            :key="`metabelow-${index}`"
                            v-html="item"
                            :class="[$style['mds-combo-box__item-metadata-item']]"
                        ></span>
                    </div>
                    <mds-icon
                        v-if="isSelected(item.value)"
                        :class="$style['mds-combo-box__selected-icon']"
                        name="check"
                        title="check"
                        size="small"
                        role="presentation"
                    />
                </li>

                <li v-show="filteredList.length === 0">No Results Found</li>
            </ul>
        </div>
    </div>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import { MDSKeyboardNavigation } from '@mds/utils-js';
    import ComboBoxDirectives from './combo-box_directives.js';
    import ComboBoxFilters from './combo-box_filters.js';

    export default {
        name: 'MdsSingleSelect',
        components: {
            MdsIcon,
        },
        props: {
            ariaDescription: {
                type: String,
            },
            ariaInputId: {
                type: String,
            },
            ariaLabel: {
                type: String,
            },
            dataSet: {
                type: Array,
            },
            disabled: {
                type: Boolean,
            },
            displayTop: {
                type: Boolean,
            },
            error: {
                type: Boolean,
            },
            id: {
                type: String,
            },
            initialSelection: {
                type: Array,
            },
            onDark: {
                type: Boolean,
            },
            placeholder: {
                type: String,
            },
            required: {
                type: Boolean,
            },
            size: {
                type: String,
            },
        },
        data() {
            return {
                searchTerm: '',
                selectedItem: null,
                showResults: false,
                highlightedItem: this.defaultHighlightedItem(),
            };
        },
        watch: {
            selectedItem(val) {
                const value = val && val.value ? [val.value] : [];
                if (!this.initialSelection || (this.initialSelection && value[0] !== this.initialSelection[0])) {
                    this.$emit('input', value);
                }
            },
            initialSelection: 'setInitialSelection',
        },
        computed: {
            ariaActiveDescendant() {
                return this.showResults ? `mds-combo-box__descendent-${this.highlightedItem}` : null;
            },
            ariaRelationshipId() {
                return this.showResults ? this.id : null;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-combo-box--error']] = this.error;

                return obj;
            },
            displayValue() {
                return this.selectedItem ? this.selectedItem.text : this.placeholder || ' ';
            },
            filteredList() {
                return this.dataSet.filter(item => {
                    var vm = this;
                    return item.text.toLowerCase().includes(vm.searchTerm.toLowerCase());
                });
            },
            sizeClass() {
                return this.$style[`mds-combo-box--${this.size}`];
            },
        },
        filters: ComboBoxFilters,
        beforeMount() {
            this.setInitialSelection();
        },
        directives: {
            clickOutside: ComboBoxDirectives.clickOutside,
            autoDirection: MDSKeyboardNavigation.autoDirection,
            keyboardNavigation: MDSKeyboardNavigation.keyboardNavigation,
        },
        methods: {
            setInitialSelection() {
                if (!this.initialSelection) {
                    return;
                }

                this.selectedItem = this.dataSet.find(element => element.value === this.initialSelection[0]);
            },
            defaultHighlightedItem() {
                if (this.dataSet.length > 0) {
                    return this.dataSet[0].value ? 0 : 1;
                }
                return -1;
            },
            setItemState(text, value) {
                if (!this.isSelected(value) && value !== null) {
                    this.selectItem(value);
                    this.removeFocus();
                    this.hideResults();
                }
            },
            openResults() {
                if (this.disabled) {
                    return;
                }

                this.showResults = true;
                this.$nextTick(() => {
                    this.$refs.input.focus();
                    if (this.selectedItem) {
                        const selectedIndex = this.dataSet.findIndex(item => item.value === this.selectedItem.value);
                        this.highlightedItem = selectedIndex;
                        this.scrollToItem(this.$refs[selectedIndex][0]);
                    }
                });
            },
            selectItem(value) {
                this.selectedItem = this.dataSet.find(element => element.value === value);
                this.searchTerm = '';
            },
            isSelected(value) {
                return this.selectedItem && this.selectedItem.value === value;
            },
            isHighlighted(item, index) {
                return item.value && index === this.highlightedItem;
            },
            removeFocus() {
                this.$refs.input.blur();
            },
            hideResults() {
                this.showResults = false;
            },
            scrollToItem(el, options = { block: 'nearest', inline: 'nearest' }) {
                if (el) {
                    el.scrollIntoView(options);
                }
            },
            isHeading(el) {
                return el.classList.contains(this.$style['mds-combo-box--heading']);
            },
            positionResults(el) {
                const selectionOffset = this.$refs.selection.getBoundingClientRect().height;
                if (this.displayTop) {
                    el.style.cssText = `bottom: ${selectionOffset}px`;
                } else {
                    el.style.cssText = `top: ${selectionOffset}px`;
                }
            },
            navigate(event) {
                let step;

                if (event.keyCode === 13 && !this.isHeading(this.$refs[this.highlightedItem][0])) {
                    event.preventDefault();
                    this.setItemState(
                        this.$refs[this.highlightedItem][0].dataset.text,
                        this.$refs[this.highlightedItem][0].dataset.value
                    );
                    return;
                }

                if (event.keyCode === 27) {
                    this.hideResults();
                    this.removeFocus();
                    return;
                }

                if (event.keyCode === 38 && this.highlightedItem > 0) {
                    step =
                        this.isHeading(this.$refs[this.highlightedItem - 1][0]) && this.highlightedItem - 2 > 0 ? 2 : 1;
                    this.highlightedItem -= step;
                } else if (event.keyCode === 40 && this.highlightedItem < this.filteredList.length - 1) {
                    step = this.isHeading(this.$refs[this.highlightedItem + 1][0]) ? 2 : 1;
                    this.highlightedItem += step;
                }
                if (this.highlightedItem > 0) {
                    this.scrollToItem(this.$refs[this.highlightedItem][0]);
                }
            },
            handleTab() {
                this.hideResults();
            },
        },
    };
</script>

<style lang="scss" module>
    @import './combo-box.scss';
</style>
